import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import styled from "styled-components";
import BoundarySelection from "../boundarySelection/BoundarySelection";
import DataSelectionProvider from "../dataSelection/DataSelectionProvider";
import LayerSelection from "../sidebar/sidebars/layerSelection/LayerSelection";
import { useAuthContext } from "../auth/AuthContext";
import { CurrentUser } from "../../types/User";
import useBannerMessageService from "../../hooks/useBannerMessageService";
import { DateTime } from "luxon";
import Sidebars from "../sidebar/sidebars";
import RoutelineSidebar from "../sidebar/sidebars/routeline/routelineSidebar";
import FeatureSidebars from "../sidebar/sidebars/FeatureSidebars";
import { useFeatureContext } from "../../context/Feature";

const BannerMessage = styled.div`
  position: relative;
  background-color: #e4a40e;
  color: white;
  padding: 5px;
  text-align: center;
  font-weight: bold;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 8px;
  top: 4px;
  background: transparent;
  border: none;
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
`;

const RightSidebarContainer = styled.div`
  display: flex;
  flex-direction: column;
  z-index: 150;
  width: 15%;
`;

export const Home = () => {
  const authContext = useAuthContext();
  const [user] = useState<CurrentUser | undefined>(authContext?.user);
  const isLoggedIn = !!user;
  const { selectedFeatures } = useFeatureContext();
  const [bannerMessage, setBannerMessage] = useState<string | null>(null);
  const bannerMessageService = useBannerMessageService();

  const location = useLocation();

  useEffect(() => {
    let timeoutId: any | undefined = undefined;

    async function loadMessage() {
      const message = await bannerMessageService.GetCurrent().catch(() => null);
      if (!message) return;

      setBannerMessage(message.message);
      const now = DateTime.local();
      const msUntilExpiry = message.displayTo.diff(now).as("milliseconds");

      if (msUntilExpiry > 0) {
        timeoutId = setTimeout(() => {
          setBannerMessage(null);
        }, msUntilExpiry);
      } else {
        setBannerMessage(null);
      }
    }

    loadMessage();

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <Container>
      {bannerMessage && (
        <BannerMessage>
          {bannerMessage}
          <CloseButton onClick={() => setBannerMessage(null)}>×</CloseButton>
        </BannerMessage>
      )}
      <Content>
        <Sidebars>
          <LayerSelection />
          <RoutelineSidebar />
        </Sidebars>
        <BoundarySelection />
        <DataSelectionProvider>
          <Outlet />
        </DataSelectionProvider>
        {selectedFeatures.length > 0 && (
          <RightSidebarContainer className="bg-light">
            <FeatureSidebars />
          </RightSidebarContainer>
        )}
      </Content>
    </Container>
  );
};
